import { FieldState } from 'final-form';
import React, { useEffect, useState } from 'react';
import { AnyObject, useForm } from 'react-final-form';
import { Col, Row } from 'reactstrap';
import { QuestionnaireItem, QuestionnaireTableItem } from '..';
import Indicator, {
  IndicatorStateOptions,
} from '../../../components/Indicator';
import {
  ItemContainer,
  ItemContainerFooter,
  ItemContainerHeader,
  ItemContainerText,
} from '../../../components/Item';
import MoreInformation from '../../../components/Item/MoreInformation';
import ErrorField from '../../../containers/Fields/ErrorField';
import {
  IQuestionnaireItemGroupComponent,
  IQuestionnaireItemTableGroupComponent,
} from '../../../modules/unsw-fhir/Questionnaire';
import {
  getSupportingLinkUris,
  isTable,
} from '../../../modules/unsw-fhir/Questionnaire/extensions';
import { generateSimpleEnableWhenPredicate } from '../../../modules/unsw-fhir/QuestionnaireResponse/helpers';
import { Conditionals } from '../../Fields/Conditional';
import { useTimeout } from '../../useTimeout/useTimeout';
import './index.css';

interface TableRenderLayoutProps extends IQuestionnaireItemTableGroupComponent {
  enabled?: boolean;
}

const isTableProps = (
  props: GroupRenderProps,
): props is TableRenderLayoutProps => isTable(props);

const getIndicatorState = (
  fieldNames: string[],
  getFieldState: (fieldName: string) => FieldState<AnyObject> | undefined,
): IndicatorStateOptions => {
  const processFieldState = (fieldName: string): IndicatorStateOptions => {
    let state: IndicatorStateOptions = 'new';
    const fieldState = getFieldState(fieldName);
    if (!fieldState) return state;
    if (
      fieldState.submitting &&
      fieldState.dirtySinceLastSubmit !== fieldState.dirty
    )
      state = 'loading';
    if (fieldState.error || fieldState.invalid) state = 'error';
    if (fieldState.initial || (fieldState.valid && fieldState.pristine))
      state = 'complete';
    return state;
  };
  let state: IndicatorStateOptions = 'new';
  const fieldStates = fieldNames.map(processFieldState);
  if (fieldStates.some(fieldState => fieldState === 'loading')) {
    state = 'loading';
  }
  if (fieldStates.some(fieldState => fieldState === 'error')) {
    state = 'error';
  }
  if (fieldStates.every(fieldState => fieldState === 'complete')) {
    state = 'complete';
  }
  return state;
};

const TableRenderLayout = (props: TableRenderLayoutProps) => {
  const { getFieldState } = useForm();
  const registeredFields = props.item
    .map(gItem => {
      const { linkId, item } = gItem;
      if (!item) return [linkId];
      return [linkId, ...item.map(tItem => tItem.linkId)];
    })
    .flat();

  return (
    <ItemContainer pulsing={props.enabled !== undefined}>
      <ItemContainerText text={props.text}></ItemContainerText>
      <ItemContainerHeader>
        <div className="d-flex flex-row-reverse justify-content-between"></div>
      </ItemContainerHeader>
      <Row noGutters={true} className="ic-item m-2">
        <Col className="tc-container">
          {props.item.map(item => (
            <QuestionnaireTableItem key={item.linkId} {...item} />
          ))}
        </Col>
      </Row>
      <ItemContainerFooter>
        <div className="d-flex flex-row-reverse align-items-center ">
          <Indicator
            state={getIndicatorState(registeredFields, getFieldState)}
          />
          <ErrorField name={props.linkId} />
        </div>
      </ItemContainerFooter>
    </ItemContainer>
  );
};

const GroupRenderLayout = (props: GroupRenderProps) => {
  const supportLinks = getSupportingLinkUris(props);
  const openSupportingLinks = (urls: string[]) => {
    urls.forEach(url => {
      window.open(url, '_blank');
    });
    return;
  };

  return (
    <>
      <h2>{props.text}</h2>
      {supportLinks.length !== 0 ? (
        <MoreInformation
          onClick={() => openSupportingLinks(supportLinks)}></MoreInformation>
      ) : null}

      {props.item.map(item => (
        <QuestionnaireItem key={item.linkId} {...item} />
      ))}
    </>
  );
};

interface GroupRenderProps extends IQuestionnaireItemGroupComponent {
  enabled?: boolean;
}

const GroupRender = (props: GroupRenderProps) => {
  let [shouldPulse, setPulse] = useState(
    props.enableWhen !== undefined && props.enableWhen.length !== 0,
  );
  let [deboucing, setDebouce] = useState(false);
  const form = useForm();

  useEffect(() => () => {
    if (props.enabled === false) {
      form.batch(() => {
        props.item.forEach(item => form.change(item.linkId, undefined));
      });
    }
  });
  useTimeout(() => setPulse(false), deboucing ? 1000 : null);

  return props.enabled || props.enabled === undefined ? (
    <div
      className={shouldPulse ? 'pulse-items' : ''}
      onMouseEnter={() => (shouldPulse ? setDebouce(true) : undefined)}
      onMouseLeave={() => (shouldPulse ? setDebouce(false) : undefined)}
      onMouseDown={() => (shouldPulse ? setPulse(false) : undefined)}>
      {isTableProps(props) ? (
        <TableRenderLayout {...props} />
      ) : (
        <GroupRenderLayout {...props} />
      )}
    </div>
  ) : null;
};

const Group = (props: IQuestionnaireItemGroupComponent) => {
  const { enableWhen } = props;

  return (
    <Conditionals
      question={props.linkId}
      enableWhens={
        enableWhen
          ? enableWhen.map(ew => ({
              when: ew.question,
              is: generateSimpleEnableWhenPredicate(ew),
            }))
          : undefined
      }>
      {({ enabled }) => <GroupRender enabled={enabled} {...props} />}
    </Conditionals>
  );
};

export default Group;
